/**
 * Render-only road curvature — Wave 2.
 *
 * The sim stays straight (lanes, collisions, kick math, spawner solvability are
 * untouched). This module maps straight sim space onto a gently curved corridor
 * so the cockpit reads an S-curve while every gameplay system sees a straight
 * road. See CURVATURE.md for the full contract.
 *
 * Math (road-distance d, metres from run start):
 *   curveX(d) = 0                              for d < 40 (run starts straight)
 *            = A1*sin(f(s)/L1) + A2*sin(f(s)/L2), s = d - 40,   otherwise
 *   tangent(d) = d curveX / dd  (analytic; 0 below 40)
 *   A1 = 22, L1 = 200, A2 = 8, L2 = 90  =>  max |tangent| ≈ 0.197 ≈ 11.2°
 *
 * f is a smooth ease-in reparametrisation of arc length, f(s) = s³/(s+K)²
 * with K = 90. It exists purely to make the seam at
 * d = 40 CONTINUOUS IN CURVATURE: a raw sine sum starts with slope
 * A1/L1 + A2/L2 = 0.199, so the tangent (and with it the camera yaw and every
 * road sub-segment heading) would STEP by 11.2° in the single frame the run
 * crossed the seam, and the ribbon would carry a permanent V-shaped kink there.
 * f(0) = f'(0) = f''(0) = 0 makes curveX C2 across the seam, and since
 * 0 ≤ f'(s) < 1 everywhere it also strictly LOWERS the tangent bound (the
 * curve far past the ramp is just the original one, phase-delayed by 2K).
 *
 * K is sized so the ramp's own curvature never exceeds the curve's natural
 * maximum κ_max = A1/L1² + A2/L2² — the entry bends no harder than the road
 * already does. The ramp's curvature peaks at
 *   (A1/L1 + A2/L2) * max f''  with  f''(s) = 6K²s/(s+K)⁴,  max f'' = 0.633/K
 * (attained at s = K/3), so the contract is
 *   K ≥ 0.633 * (A1/L1 + A2/L2) / κ_max = 0.633 * 0.19889 / 0.0015383 ≈ 82.
 * K = 90 clears that with margin: swept numerically over d ∈ [0, 5000) the
 * per-metre tangent change peaks at 0.00153 /m — the far-field κ_max itself,
 * i.e. the ramp is NOT the tightest part of the road. (At the old A1 = 11 /
 * L1 = 260 / A2 = 4.5 / L2 = 110 the same contract asked for K ≥ 99, which is
 * why K was 120 then; raising the amplitudes raises κ_max faster than it
 * raises the slope, so the required K went DOWN.)
 *
 * For a player at distance D, an object at sim/world z (negative ahead) is at
 * road-distance r = D - z. The renderer shifts it laterally by
 *   curveOffset(z, D)   = curveX(r) - curveX(D)
 * and yaws it by
 *   curveHeading(z, D)  = -atan(tangent(r))   (absolute road heading at r)
 * so a driving vehicle keeps a constant angle to the road as r changes, and the
 * camera (yawed to -atan(tangent(D))) sees the road bend ahead. Road sub-
 * segments take the ABSOLUTE heading -atan(tangent(r_mid)) instead, so the
 * ribbon itself aligns with the curve; the relative-to-camera term emerges from
 * the camera's own yaw.
 *
 * Zero allocation: every function is pure arithmetic over numbers.
 */

// Curve amplitudes (m) and wavelengths (m).
//
// 2026-07-31 art direction: MUCH curvier roads. The previous 11 / 260 / 4.5 /
// 110 (max ≈ 4.8°) read as an almost-straight highway — the bend was there in
// the math but barely visible in a 9:16 cockpit view. Doubling the amplitudes
// and shortening the wavelengths takes max |tangent| to ≈ 0.197 (≈ 11.2°),
// which is a pronounced S-curve on screen while staying far short of the
// yaw-swing territory that fought the kick/crash read (that was a *step*
// artifact at the seam, now C2, not the steady-state curvature).
const A1 = 22;
const L1 = 200;
const A2 = 8;
const L2 = 90;
// Straight prefix: no curvature for the first 40 m of the run. Lowered from
// 150 so the road is visibly bending within the first couple of seconds
// instead of opening on a long straight (the ease-in below still means the
// bend arrives gradually: |curveX| passes 1 m at d ≈ 86 and 5 m at d ≈ 136).
const STRAIGHT_UP_TO = 40;
// Ease-in length constant (m) for the seam reparametrisation f (see header).
const EASE_K = 90;
// Precomputed 1/L for the analytic tangent (avoid per-call division).
const INV_L1 = 1 / L1;
const INV_L2 = 1 / L2;

/**
 * Smooth ease-in of arc length past the seam: f(s) = s³/(s+K)².
 * f(0) = f'(0) = f''(0) = 0 and f'(s) → 1, so the curve starts perfectly
 * straight and settles onto the plain sine sum (delayed by 2K).
 */
function ease(s: number): number {
  const k = s + EASE_K;
  return (s * s * s) / (k * k);
}

/** df/ds = s²(s+3K)/(s+K)³ — in [0, 1), so it never amplifies the tangent. */
function easeDeriv(s: number): number {
  const k = s + EASE_K;
  return (s * s * (s + 3 * EASE_K)) / (k * k * k);
}

/**
 * Lateral offset (m) of the corridor centerline at road-distance d.
 * 0 for d < 40 (the run starts straight); a smooth sum of two sines beyond,
 * eased in so the seam is C2 (no curvature kink in the ribbon at r = 40).
 */
export function curveX(d: number): number {
  if (d < STRAIGHT_UP_TO) return 0;
  const s = ease(d - STRAIGHT_UP_TO);
  return A1 * Math.sin(s / L1) + A2 * Math.sin(s / L2);
}

/**
 * Analytic d(curveX)/dd — the lateral slope of the centerline at road-distance
 * d. 0 below 40 m, and 0 at the seam itself (chain rule through the ease, whose
 * derivative vanishes there). Max |tangent| ≲ A1/L1 + A2/L2 ≈ 0.199 (≈ 11.2°);
 * the measured peak over a 5 km sweep is 0.1974 (≈ 11.17°).
 */
export function curveTangent(d: number): number {
  if (d < STRAIGHT_UP_TO) return 0;
  const u = d - STRAIGHT_UP_TO;
  const s = ease(u);
  return easeDeriv(u) * ((A1 * INV_L1) * Math.cos(s / L1) + (A2 * INV_L2) * Math.cos(s / L2));
}

/**
 * Lateral render offset (m) for an object at world/sim z relative to a player
 * at distance D. r = D - z (z negative ahead → r > D). The player's own
 * position (z = 0, r = D) maps to 0, so the cockpit stays centered.
 */
export function curveOffset(z: number, D: number): number {
  return curveX(D - z) - curveX(D);
}

/**
 * Absolute curve heading (radians about Y) for a road-aligned object at
 * world/sim z for a player at distance D: -atan(tangent(r)), r = D - z.
 *
 * This matches the road ribbon's own sub-segment heading (world.ts writes
 * rotation.y = -atan(tangent(rMid))): the scene is posed in WORLD space and
 * the camera separately yaws by -atan(tangent(D)), so road-aligned content
 * (vehicles, roadside props, the rider shadow) must use the ABSOLUTE heading
 * at its own r — NOT a delta relative to the player's heading. (The old
 * curveYawDelta added atan(tangent(D)), leaving everything rotated off the
 * road by the player's heading while riding a bend.)
 *
 * Compose by adding to rotation.y (driving) or premultiplying a Y-quaternion
 * (kicked tumble).
 */
export function curveHeading(z: number, D: number): number {
  return -Math.atan(curveTangent(D - z));
}
